import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminDashboardService } from '../../services/adminDashboardService';
import AdminNavigation from '../../components/ui/AdminNavigation';
import KPICard from './components/KPICard';
import ShipmentStatusCards from './components/ShipmentStatusCards';
import ContainersTable from './components/ContainersTable';
import TopClientsTable from './components/TopClientsTable';
import LatestShipmentsTable from './components/LatestShipmentsTable';
import IncidentDeliveryTasksTable from './components/IncidentDeliveryTasksTable';

const ComprehensiveAdminKPIDashboard = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [incidentTasks, setIncidentTasks] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [incidentsLoading, setIncidentsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboardData();
    loadIncidentData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminDashboardService?.getDashboardMetrics();
      setMetrics(result?.data);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err?.message || 'Erreur lors du chargement du tableau de bord');
    } finally {
      setLoading(false);
    }
  };

  const loadIncidentData = async () => {
    try {
      setIncidentsLoading(true);
      const [incidentsData, agentsData] = await Promise.all([
        adminDashboardService?.getIncidentDeliveryTasks(),
        adminDashboardService?.getAgentsForFilter()
      ]);
      setIncidentTasks(incidentsData);
      setAgents(agentsData);
    } catch (err) {
      console.error('Error loading incident data:', err);
    } finally {
      setIncidentsLoading(false);
    }
  };

  const handleRefreshIncidents = () => {
    loadIncidentData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <AdminNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Chargement du tableau de bord...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <AdminNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center max-w-md">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
              <p className="text-red-800 font-medium mb-2">Erreur</p>
              <p className="text-red-600 text-sm mb-4">{error}</p>
              <button
                onClick={loadDashboardData}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Réessayer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Tableau de bord ADT</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">Vue d'ensemble de l'activité</p>
        </div>

        {/* KPI Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <KPICard
            title="CA encaissé (mois courant)"
            value={`${metrics?.kpis?.revenueCollectedThisMonth?.toFixed(2)} €`}
            icon="euro"
            color="green"
          />
          <KPICard
            title="Reste à encaisser"
            value={`${metrics?.kpis?.remainingToCollect?.toFixed(2)} €`}
            icon="alert"
            color="orange"
          />
          <KPICard
            title="Envois du mois"
            value={metrics?.kpis?.shipmentsThisMonth}
            icon="package"
            color="blue"
          />
          <KPICard
            title="Conteneurs ouverts"
            value={metrics?.kpis?.openContainers}
            icon="container"
            color="purple"
          />
        </div>

        {/* Shipment Status Cards */}
        <ShipmentStatusCards statusCounts={metrics?.statusCounts} />

        {/* Tables Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <LatestShipmentsTable 
            shipments={metrics?.latestShipments}
            onShipmentClick={(shipmentId) => navigate(`/admin/shipments/${shipmentId}`)}
          />
          <TopClientsTable clients={metrics?.topClients} />
        </div>

        {/* Containers Table */}
        <div className="mb-6 sm:mb-8">
          <ContainersTable 
            containers={metrics?.containers}
            onContainerClick={(containerId) => navigate(`/admin/containers/${containerId}`)}
          />
        </div>

        {/* Incident Delivery Tasks */}
        <div className="mb-6 sm:mb-8">
          <IncidentDeliveryTasksTable 
            tasks={metrics?.incidentDeliveryTasks}
            incidents={incidentTasks}
            agents={agents}
            onRefresh={handleRefreshIncidents}
            loading={incidentsLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default ComprehensiveAdminKPIDashboard;